import { BlockSuiteError, ErrorCode } from '../exceptions/index.js';

/**
 * XYWH represents the x, y, width, and height of an element or block.
 */
export type XYWH = [number, number, number, number];

/**
 * SerializedXYWH is a string that represents the x, y, width, and height of a block.
 */
export type SerializedXYWH = `[${number},${number},${number},${number}]`;

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

export function isXYWH(value: unknown): value is XYWH {
  return (
    Array.isArray(value) && value.length === 4 && value.every(isFiniteNumber)
  );
}

export function parseXYWH(value: unknown): XYWH | null {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as unknown;
    return isXYWH(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function isSerializedXYWH(value: unknown): value is SerializedXYWH {
  return parseXYWH(value) !== null;
}

export type BlockXYWHValidationContext = {
  blockId?: string;
  flavour?: string;
  key?: string;
};

function formatBlockXYWHValidationContext(context: BlockXYWHValidationContext) {
  const parts = [
    context.flavour ? `flavour=${context.flavour}` : null,
    context.blockId ? `blockId=${context.blockId}` : null,
    context.key ? `key=${context.key}` : null,
  ].filter(Boolean);

  return parts.length ? ` (${parts.join(', ')})` : '';
}

export function assertValidBlockXYWH(
  value: unknown,
  context: BlockXYWHValidationContext = {}
): asserts value is SerializedXYWH {
  const xywh = parseXYWH(value);
  if (!xywh) {
    throw new BlockSuiteError(
      ErrorCode.ModelCRUDError,
      `Invalid block xywh${formatBlockXYWHValidationContext(context)}: expected serialized [x,y,w,h] with finite numbers, got ${String(
        value
      )}`
    );
  }

  if (xywh[2] <= 0 || xywh[3] <= 0) {
    throw new BlockSuiteError(
      ErrorCode.ModelCRUDError,
      `Invalid block xywh${formatBlockXYWHValidationContext(context)}: width and height must be positive, got ${String(
        value
      )}`
    );
  }
}

export function serializeXYWH(
  x: number,
  y: number,
  w: number,
  h: number
): SerializedXYWH {
  if (!isXYWH([x, y, w, h])) {
    throw new TypeError(
      `Cannot serialize invalid xywh [${String(x)},${String(y)},${String(w)},${String(h)}]`
    );
  }

  return `[${x},${y},${w},${h}]`;
}

export function deserializeXYWH(xywh: unknown): XYWH {
  const parsed = parseXYWH(xywh);
  if (!parsed) {
    throw new TypeError(`Cannot deserialize invalid xywh: ${String(xywh)}`);
  }
  return parsed;
}
